import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Portal,
  CloseButton,
  Input,
  Button,
  Flex,
  VStack,
  HStack,
  Text,
  Box,
  IconButton,
  Separator,
  Textarea,
  Heading,
  Image
} from '@chakra-ui/react';
import { LuPlus, LuTrash2, LuEye } from 'react-icons/lu';
import { COLORS } from '../../../theme';
import { EVENT_SAMPLES } from '../../../constants/event-samples';
import CustomerEvent from '../../customer-event';

export default function EventDialog({
  isOpen,
  onClose,
  mode,
  selectedKey,
  initialValue,
  isSaving,
  onSave
}) {
  const [formData, setFormData] = useState({
    title: '',
    youtubeDetails: [],
    photos: [],
    iframes: []
  });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialValue && initialValue !== '{\n  \n}') {
        try {
          const parsed = JSON.parse(initialValue);
          setFormData({
            title: parsed.title || '',
            youtubeDetails: parsed.youtubeDetails || [],
            photos: parsed.photos || [],
            iframes: parsed.iframes || []
          });
        } catch (e) {
          console.error('Failed to parse initial JSON', e);
        }
      } else {
        setFormData({
          title: '',
          youtubeDetails: [],
          photos: [],
          iframes: []
        });
      }
    }
  }, [isOpen, initialValue]);

  const handleSave = () => {
    const jsonString = JSON.stringify(formData, null, 2);
    if (mode === 'create') {
      const generatedId = `event-${Math.random().toString(36).substring(2, 9)}`;
      onSave(generatedId, jsonString);
    } else {
      onSave(selectedKey, jsonString);
    }
  };

  const addYoutubeField = () => {
    setFormData({
      ...formData,
      youtubeDetails: [
        ...formData.youtubeDetails,
        { youtubeId: '', name: '', date: '', location: '', description: '' }
      ]
    });
  };

  const removeYoutubeField = (index) => {
    const updated = [...formData.youtubeDetails];
    updated.splice(index, 1);
    setFormData({ ...formData, youtubeDetails: updated });
  };

  const updateYoutubeField = (index, field, value) => {
    const updated = [...formData.youtubeDetails];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, youtubeDetails: updated });
  };

  const addPhotoField = () => {
    setFormData({
      ...formData,
      photos: [...formData.photos, '']
    });
  };

  const removePhotoField = (index) => {
    const updated = [...formData.photos];
    updated.splice(index, 1);
    setFormData({ ...formData, photos: updated });
  };

  const updatePhotoField = (index, value) => {
    const updated = [...formData.photos];
    updated[index] = value;
    setFormData({ ...formData, photos: updated });
  };

  const addIframeField = () => {
    setFormData({
      ...formData,
      iframes: [
        ...formData.iframes,
        {
          id: '',
          view: 'grid',
          name: '',
          description: '',
          aspectRatio: 16 / 9,
          height: '600px',
          allow: 'fullscreen'
        }
      ]
    });
  };

  const removeIframeField = (index) => {
    const updated = [...formData.iframes];
    updated.splice(index, 1);
    setFormData({ ...formData, iframes: updated });
  };

  const updateIframeField = (index, field, value) => {
    const updated = [...formData.iframes];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, iframes: updated });
  };

  const loadSample = (sampleKey) => {
    const sample = EVENT_SAMPLES[sampleKey];
    if (sample) {
      setFormData({
        title: sample.title,
        youtubeDetails: [...sample.youtubeDetails],
        photos: [...sample.photos],
        iframes: [...(sample.iframes || [])]
      });
    }
  };

  const dialogTitle =
    mode === 'create' ? 'Create New Event' : `Edit Event — ${selectedKey}`;
  const actionText = mode === 'create' ? 'Create' : 'Save';

  // Dynamic colors based on system preference via Chakra tokens
  const sectionBg = {
    _light: 'gray.50',
    _dark: 'gray.800'
  };
  const inputBg = {
    _light: 'white',
    _dark: 'gray.900'
  };
  const labelColor = {
    _light: 'gray.600',
    _dark: 'gray.400'
  };
  const borderColor = {
    _light: 'gray.200',
    _dark: 'gray.700'
  };

  return (
    <Dialog.Root
      size="lg"
      placement="center"
      motionPreset="slide-in-bottom"
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottomWidth="1px" borderColor={borderColor}>
              <Dialog.Title>{dialogTitle}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={6}>
              <VStack gap={6} align="stretch">
                {/* Basic Info */}
                <Box>
                  <Heading size="sm" mb={3} color={labelColor}>
                    Basic Information
                  </Heading>
                  <VStack gap={4} align="stretch">
                    {mode === 'create' && (
                      <Box pb={2}>
                        <Text fontSize="xs" fontWeight="bold" mb={2} ml={1}>
                          QUICK TEMPLATES
                        </Text>
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            variant="subtle"
                            colorPalette="purple"
                            onClick={() => loadSample('sampleEvent')}
                          >
                            Sample Event
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => loadSample('empty')}
                          >
                            Clear
                          </Button>
                        </HStack>
                      </Box>
                    )}
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb={1} ml={1}>
                        EVENT TITLE
                      </Text>
                      <Input
                        placeholder="Hero Title"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        bg={inputBg}
                      />
                    </Box>
                  </VStack>
                </Box>

                <Separator borderColor={borderColor} />

                {/* Youtube Videos */}
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading size="sm" color={labelColor}>
                      YouTube Videos
                    </Heading>
                    <Button
                      size="xs"
                      colorPalette="orange"
                      variant="ghost"
                      onClick={addYoutubeField}
                    >
                      <LuPlus /> Add Video
                    </Button>
                  </Flex>

                  <VStack gap={4} align="stretch">
                    {formData.youtubeDetails.map((video, index) => (
                      <Box
                        key={index}
                        p={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        position="relative"
                        bg={sectionBg}
                      >
                        <Box position="absolute" top={2} right={2}>
                          <IconButton
                            aria-label="Remove video"
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() => removeYoutubeField(index)}
                          >
                            <LuTrash2 />
                          </IconButton>
                        </Box>
                        <VStack gap={3} align="stretch">
                          <HStack gap={3}>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                YouTube ID
                              </Text>
                              <HStack gap={2}>
                                <Box
                                  w="40px"
                                  h="30px"
                                  borderRadius="sm"
                                  overflow="hidden"
                                  flexShrink={0}
                                  bg="black"
                                >
                                  {video.youtubeId ? (
                                    <Image
                                      src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                                      alt="YT Preview"
                                      w="full"
                                      h="full"
                                      objectFit="cover"
                                    />
                                  ) : (
                                    <Box w="full" h="full" bg="gray.800" />
                                  )}
                                </Box>
                                <Input
                                  size="sm"
                                  placeholder="BTyPHTpoO5s"
                                  value={video.youtubeId}
                                  onChange={(e) =>
                                    updateYoutubeField(
                                      index,
                                      'youtubeId',
                                      e.target.value
                                    )
                                  }
                                  bg={inputBg}
                                />
                              </HStack>
                            </Box>
                            <Box flex={2}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Display Name
                              </Text>
                              <Input
                                size="sm"
                                placeholder="Live Event Coverage"
                                value={video.name}
                                onChange={(e) =>
                                  updateYoutubeField(
                                    index,
                                    'name',
                                    e.target.value
                                  )
                                }
                                bg={inputBg}
                              />
                            </Box>
                          </HStack>
                          <HStack gap={3}>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Date
                              </Text>
                              <Input
                                size="sm"
                                placeholder="Sept 06, 2025"
                                value={video.date}
                                onChange={(e) =>
                                  updateYoutubeField(
                                    index,
                                    'date',
                                    e.target.value
                                  )
                                }
                                bg={inputBg}
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Location
                              </Text>
                              <Input
                                size="sm"
                                placeholder="Bentonville, AR"
                                value={video.location}
                                onChange={(e) =>
                                  updateYoutubeField(
                                    index,
                                    'location',
                                    e.target.value
                                  )
                                }
                                bg={inputBg}
                              />
                            </Box>
                          </HStack>
                          <Box>
                            <Text fontSize="xs" fontWeight="bold" mb={1}>
                              Description
                            </Text>
                            <Textarea
                              size="sm"
                              placeholder="Brief description..."
                              value={video.description}
                              onChange={(e) =>
                                updateYoutubeField(
                                  index,
                                  'description',
                                  e.target.value
                                )
                              }
                              bg={inputBg}
                              rows={2}
                            />
                          </Box>
                        </VStack>
                      </Box>
                    ))}
                    {formData.youtubeDetails.length === 0 && (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                        borderStyle="dashed"
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        No videos added yet
                      </Text>
                    )}
                  </VStack>
                </Box>

                <Separator borderColor={borderColor} />

                {/* Photos */}
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading size="sm" color={labelColor}>
                      Photo Gallery
                    </Heading>
                    <Button
                      size="xs"
                      colorPalette="orange"
                      variant="ghost"
                      onClick={addPhotoField}
                    >
                      <LuPlus /> Add Photo URL
                    </Button>
                  </Flex>

                  <VStack gap={3} align="stretch">
                    {formData.photos.map((photoUrl, index) => (
                      <HStack key={index} gap={3} w="full">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="md"
                          overflow="hidden"
                          flexShrink={0}
                          bg={sectionBg}
                          borderWidth="1px"
                          borderColor={borderColor}
                        >
                          {photoUrl ? (
                            <Image
                              src={photoUrl}
                              alt={`Preview ${index}`}
                              w="full"
                              h="full"
                              objectFit="cover"
                              fallback={<Box w="full" h="full" bg="gray.100" />}
                            />
                          ) : (
                            <Flex
                              w="full"
                              h="full"
                              align="center"
                              justify="center"
                              color="gray.400"
                            >
                              <LuPlus size={12} />
                            </Flex>
                          )}
                        </Box>
                        <Input
                          size="sm"
                          placeholder="https://images.unsplash.com/..."
                          value={photoUrl}
                          onChange={(e) =>
                            updatePhotoField(index, e.target.value)
                          }
                          bg={inputBg}
                          flex={1}
                        />
                        <IconButton
                          aria-label="Remove photo"
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => removePhotoField(index)}
                        >
                          <LuTrash2 />
                        </IconButton>
                      </HStack>
                    ))}
                    {formData.photos.length === 0 && (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                        borderStyle="dashed"
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        No photos added yet
                      </Text>
                    )}
                  </VStack>
                </Box>

                <Separator borderColor={borderColor} />

                {/* Embedded iframes */}
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Heading size="sm" color={labelColor}>
                      Embedded Content (iframes)
                    </Heading>
                    <Button
                      size="xs"
                      colorPalette="orange"
                      variant="ghost"
                      onClick={addIframeField}
                    >
                      <LuPlus /> Add iframe
                    </Button>
                  </Flex>

                  <VStack gap={4} align="stretch">
                    {formData.iframes.map((embed, index) => (
                      <Box
                        key={index}
                        p={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        position="relative"
                        bg={sectionBg}
                      >
                        <Box position="absolute" top={2} right={2}>
                          <IconButton
                            aria-label="Remove iframe"
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() => removeIframeField(index)}
                          >
                            <LuTrash2 />
                          </IconButton>
                        </Box>
                        <VStack gap={3} align="stretch">
                          <HStack gap={3}>
                            <Box flex={2}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Google Drive Folder ID
                              </Text>
                              <Input
                                size="sm"
                                placeholder="1ABC_FOLDER_ID_1234567890"
                                value={embed.id}
                                onChange={(e) =>
                                  updateIframeField(index, 'id', e.target.value)
                                }
                                bg={inputBg}
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                View Type
                              </Text>
                              <Flex gap={2}>
                                <Button
                                  size="sm"
                                  variant={embed.view === 'grid' ? 'solid' : 'outline'}
                                  colorPalette="blue"
                                  onClick={() =>
                                    updateIframeField(index, 'view', 'grid')
                                  }
                                  flex={1}
                                >
                                  Grid
                                </Button>
                                <Button
                                  size="sm"
                                  variant={embed.view === 'list' ? 'solid' : 'outline'}
                                  colorPalette="blue"
                                  onClick={() =>
                                    updateIframeField(index, 'view', 'list')
                                  }
                                  flex={1}
                                >
                                  List
                                </Button>
                              </Flex>
                            </Box>
                          </HStack>
                          <HStack gap={3}>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Display Name
                              </Text>
                              <Input
                                size="sm"
                                placeholder="e.g., Event Gallery"
                                value={embed.name}
                                onChange={(e) =>
                                  updateIframeField(
                                    index,
                                    'name',
                                    e.target.value
                                  )
                                }
                                bg={inputBg}
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="xs" fontWeight="bold" mb={1}>
                                Height (px)
                              </Text>
                              <Input
                                size="sm"
                                placeholder="600"
                                value={embed.height}
                                onChange={(e) =>
                                  updateIframeField(
                                    index,
                                    'height',
                                    e.target.value
                                  )
                                }
                                bg={inputBg}
                              />
                            </Box>
                          </HStack>
                          <Box>
                            <Text fontSize="xs" fontWeight="bold" mb={1}>
                              Description
                            </Text>
                            <Textarea
                              size="sm"
                              placeholder="Brief description..."
                              value={embed.description}
                              onChange={(e) =>
                                updateIframeField(
                                  index,
                                  'description',
                                  e.target.value
                                )
                              }
                              bg={inputBg}
                              rows={2}
                            />
                          </Box>
                        </VStack>
                      </Box>
                    ))}
                    {formData.iframes.length === 0 && (
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        py={4}
                        borderStyle="dashed"
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        No iframes added yet
                      </Text>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" borderColor={borderColor}>
              <Flex justify="space-between" align="center" w="full">
                <Button
                  onClick={() => setIsPreviewOpen(true)}
                  variant="outline"
                  colorPalette="blue"
                  size="sm"
                >
                  <LuEye /> Preview
                </Button>
                <Flex gap={3}>
                  <Button onClick={onClose} variant="ghost" colorPalette="gray">
                    Cancel
                  </Button>
                  <Button
                    colorPalette="orange"
                    bg={COLORS.brand.primary}
                    _hover={{ bg: COLORS.brand.hover }}
                    onClick={handleSave}
                    loading={isSaving}
                    px={8}
                  >
                    {actionText}
                  </Button>
                </Flex>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>

      {/* Preview Dialog */}
      <Dialog.Root
        open={isPreviewOpen}
        onOpenChange={(details) => !details.open && setIsPreviewOpen(false)}
        size="full"
        motionPreset="slide-in-bottom"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              bg={sectionBg._light}
              _dark={{ bg: sectionBg._dark }}
            >
              <Dialog.Header borderBottomWidth="1px" borderColor={borderColor}>
                <Flex justify="space-between" align="center" w="full">
                  <Dialog.Title>Live Preview</Dialog.Title>
                  <Button size="sm" onClick={() => setIsPreviewOpen(false)}>
                    Close Preview
                  </Button>
                </Flex>
              </Dialog.Header>
              <Dialog.Body p={0} overflowY="auto">
                <Box py={10}>
                  <CustomerEvent eventData={formData} />
                </Box>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Dialog.Root>
  );
}
